import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, MailCheck, MailX, Loader2, Send } from "lucide-react";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "loading" : "no-token");
  const [errorMsg, setErrorMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        const { data } = await api.post("/customers/confirm-email", { token });
        saveAuth(data.token, data.user);
        setState("success");
        toast.success(`¡Cuenta activada! Bienvenido, ${data.user.name}`);
        setTimeout(() => navigate("/mi-cuenta"), 1500);
      } catch (err) {
        const detail = err?.response?.data?.detail || "No se pudo confirmar tu correo.";
        setErrorMsg(typeof detail === "string" ? detail : "El enlace es inválido o expiró.");
        setState("error");
      }
    })();
  }, [token, navigate]);

  const resend = async (e) => {
    e.preventDefault();
    const email = resendEmail.trim();
    if (!email) {
      toast.error("Ingresa tu correo");
      return;
    }
    setResending(true);
    try {
      await api.post("/customers/resend-confirmation", { email });
      toast.success("Si el correo existe, te enviamos un nuevo enlace de confirmación.");
    } catch {
      toast.error("Error al reenviar el correo");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      <BrandHeader />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="glass rounded-xl p-8 md:p-10 max-w-md w-full" data-testid="verify-email-panel">
          {state === "loading" && (
            <div className="text-center py-6">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-yellow-400 mb-4" />
              <h1 className="font-display font-black text-2xl mb-2">Verificando tu correo…</h1>
              <p className="text-zinc-400 text-sm">Un momento por favor.</p>
            </div>
          )}

          {state === "success" && (
            <div className="text-center py-6" data-testid="verify-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <h1 className="font-display font-black text-2xl mb-2">¡Correo confirmado!</h1>
              <p className="text-zinc-400 text-sm mb-4">
                Tu cuenta está activa. Te redirigimos a tu portal…
              </p>
            </div>
          )}

          {state === "error" && (
            <div data-testid="verify-error">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
                  <MailX className="w-7 h-7 text-red-400" />
                </div>
                <h1 className="font-display font-black text-2xl mb-2">Enlace no válido</h1>
                <p className="text-red-300/90 text-sm">{errorMsg}</p>
              </div>
              <form onSubmit={resend} className="space-y-3 border-t border-white/10 pt-5">
                <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                  Reenviar enlace de confirmación
                </Label>
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  required
                  data-testid="resend-email-input"
                  className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400"
                />
                <Button
                  type="submit"
                  disabled={resending}
                  data-testid="resend-submit-btn"
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
                >
                  {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {resending ? "Enviando…" : "Enviar nuevo enlace"}
                </Button>
              </form>
            </div>
          )}

          {state === "no-token" && (
            <div data-testid="verify-no-token">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-yellow-400/15 border border-yellow-400/40 flex items-center justify-center mx-auto mb-4">
                  <MailCheck className="w-7 h-7 text-yellow-400" />
                </div>
                <h1 className="font-display font-black text-2xl mb-2">Confirma tu correo</h1>
                <p className="text-zinc-400 text-sm">
                  Ingresa tu correo para reenviar el enlace de confirmación.
                </p>
              </div>
              <form onSubmit={resend} className="space-y-3">
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  required
                  data-testid="resend-email-input"
                  className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400"
                />
                <Button
                  type="submit"
                  disabled={resending}
                  data-testid="resend-submit-btn"
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
                >
                  {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {resending ? "Enviando…" : "Enviar enlace de confirmación"}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
